import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    ShoppingCart, Package, Building2, ChevronDown, Loader2,
    Plus, Trash2, CheckCircle, AlertTriangle,
    Upload, MapPin, BookOpen, X,
    RefreshCw, Send, Search, Truck,
} from 'lucide-react';

import CustomDatePicker from '../../components/CustomDatePicker';
import { uploadFileToS3 } from '../../api/commonAPI/awsUploadAPI';
import { S3_FOLDERS } from '../../config/s3Config';
import { getVendorGSTNos, getCompanySupGSTNos } from '../../api/SupplierPOAPI/supplierPOCreationAPI';

import {
    fetchSupplierIndents,
    checkPartIndentExist,
    fetchPOGridData,
    fetchSupplierVendors,
    checkPOBudget,
    submitSupplierPO,
    fetchTermHeads,
    fetchTermRemarks,
    fetchUploadDocValue,
    clearPartIndentCheck,
    clearGridData,
    clearTermRemarks,
    clearBudgetResult,
    clearSaveResult,
    resetCreation,
    selectIndentList,
    selectPartIndentList,
    selectPartIndentCheck,
    selectPOGridData,
    selectVendors,
    selectTermHeads,
    selectTermRemarks,
    selectSaveResult,
    selectUploadDocValue,
    selectPOCreationLoading,
    selectPOCreationErrors,
} from '../../slices/supplierPOSlice/supplierPOCreationSlice';

// ── Shared UI primitives ──────────────────────────────────────────────────────

const inputCls =
    'w-full px-3 py-2 rounded-lg border text-sm bg-white dark:bg-gray-800 ' +
    'text-gray-800 dark:text-gray-100 border-gray-300 dark:border-gray-600 ' +
    'focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 ' +
    'disabled:opacity-60 disabled:cursor-not-allowed transition-colors';

const Label = ({ children, required }) => (
    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">
        {children}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
);

const SectionCard = ({ icon: Icon, title, subtitle, children, accent = 'indigo' }) => {
    const colors = {
        indigo: 'from-indigo-50 to-indigo-100 dark:from-indigo-900/20 dark:to-indigo-800/20 border-indigo-200 dark:border-indigo-700 text-indigo-800 dark:text-indigo-300',
        green:  'from-green-50  to-green-100  dark:from-green-900/20  dark:to-green-800/20  border-green-200  dark:border-green-700  text-green-800  dark:text-green-300',
        purple: 'from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 border-purple-200 dark:border-purple-700 text-purple-800 dark:text-purple-300',
        amber:  'from-amber-50  to-amber-100  dark:from-amber-900/20  dark:to-amber-800/20  border-amber-200  dark:border-amber-700  text-amber-800  dark:text-amber-300',
        teal:   'from-teal-50   to-teal-100   dark:from-teal-900/20   dark:to-teal-800/20   border-teal-200   dark:border-teal-700   text-teal-800   dark:text-teal-300',
        rose:   'from-rose-50   to-rose-100   dark:from-rose-900/20   dark:to-rose-800/20   border-rose-200   dark:border-rose-700   text-rose-800   dark:text-rose-300',
    };
    const cls = colors[accent] || colors.indigo;
    return (
        <div className={`bg-gradient-to-br ${cls} rounded-xl border p-5 space-y-4`}>
            <div className="flex items-center gap-2">
                <Icon className="w-4 h-4" />
                <h3 className="font-semibold text-sm">{title}</h3>
                {subtitle && <span className="text-xs opacity-70 ml-1">{subtitle}</span>}
            </div>
            {children}
        </div>
    );
};

const SpinnerIcon = () => <Loader2 className="w-4 h-4 animate-spin" />;

// Extract CCType from the menu path string
const parseCCType = (menuData) => {
    const path = menuData?.path || '';
    const m = path.match(/[?&]CCType=([^&]+)/i);
    if (m) return m[1].toUpperCase();
    if (path.toLowerCase().includes('npcc')) return 'NPCC';
    return 'PCC';
};

// Extract the cost center prefix / indent segment from an IndentNo,
// e.g. "CC-01/2026-27/86" -> CC "CC-01", Indent "86"
// (spInsertSupplierPO derives @CCCode/@Indent the same way — the API is
// stateless so the frontend must supply both explicitly.)
const parseCCPrefix = (indentNo) => (indentNo || '').split('/')[0];
const parseIndentSegment = (indentNo) => (indentNo || '').split('/')[2] || '';

const todayFormatted = () => {
    const d = new Date();
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${String(d.getDate()).padStart(2,'0')}-${months[d.getMonth()]}-${d.getFullYear()}`;
};

const getFileExt = (name) => (name || '').split('.').pop().toLowerCase();

// ── Main Component ────────────────────────────────────────────────────────────

const SupplierPOCreation = ({ menuData }) => {
    const dispatch   = useDispatch();
    const { userData } = useSelector((s) => s.auth);

    const roleId = userData?.roleId || userData?.RID || '';
    const userId = userData?.UID   || userData?.uid  || '';
    const ccType = parseCCType(menuData);

    // Redux state
    const indentList      = useSelector(selectIndentList);
    const partIndentList  = useSelector(selectPartIndentList);
    const partIndentCheck = useSelector(selectPartIndentCheck);
    const gridData        = useSelector(selectPOGridData);
    const vendors         = useSelector(selectVendors);
    const termHeads       = useSelector(selectTermHeads);
    const termRemarks     = useSelector(selectTermRemarks);
    const saveResult      = useSelector(selectSaveResult);
    const uploadDocValue  = useSelector(selectUploadDocValue);
    const loading         = useSelector(selectPOCreationLoading);
    const errors          = useSelector(selectPOCreationErrors);

    // ── Local state ───────────────────────────────────────────────────────────

    const [activeTab, setActiveTab]                   = useState('new'); // 'new' | 'part'
    const [selectedIndentNo, setSelectedIndentNo]     = useState('');
    const [loadedIndentNo, setLoadedIndentNo]         = useState('');
    const [showPartWarning, setShowPartWarning]       = useState(false);
    const [pendingIndentNo, setPendingIndentNo]       = useState('');

    // Editable grid rows keyed by IndentListId
    const [rowEdits, setRowEdits] = useState({});

    // Which rows (by IndentListId) are checked for inclusion in this PO
    const [selectedRows, setSelectedRows] = useState({});

    // Header form
    const [header, setHeader] = useState({
        vendorCode: '', vendorName: '',
        poDate: '', poExpireDate: '', refNo: '', refDate: '',
        paymentType: '',
        mrrType: 'Single',
        lcApplicable: '',
    });

    // Invoice address & site (delivery) address — two distinct addresses in the SP
    const [invoice, setInvoice] = useState({ address1: '', address2: '', gstNo: '', mobileNo: '' });
    const [site, setSite]       = useState({ address1: '', address2: '', contact: '', mobileNo: '' });

    // Legacy "Is GST Applicable?" → Company GST (GetCompanySupGSTNos) + Vendor GST (GetVendorGSTNos);
    // their ids/numbers go to spInsertSupplierPO as @InvgstId/@Invgstno/@VengstId/@Vengstno.
    const [gstApplicable, setGstApplicable] = useState('');
    const [companyGstList, setCompanyGstList] = useState([]);
    const [vendorGstList, setVendorGstList] = useState([]);
    const [companyGst, setCompanyGst] = useState('');
    const [vendorGst, setVendorGst] = useState('');
    const selectedCompanyGst = companyGstList.find((g) => String(g.GSTVal) === String(companyGst));
    const selectedVendorGst = vendorGstList.find((g) => String(g.VenGSTVal) === String(vendorGst));

    // Custom Terms & Conditions lines -> Remarks (pipe-joined)
    const [termsLines, setTermsLines] = useState([{ id: Date.now(), text: '' }]);

    // Predefined T&C
    const [predefinedTermsExist, setPredefinedTermsExist] = useState(''); // '' | 'Yes' | 'No'
    const [selectedHeadId, setSelectedHeadId]         = useState('');
    const [selectedHeadName, setSelectedHeadName]     = useState('');
    const [appliedPreferredLines, setAppliedPreferredLines] = useState([]); // -> PreferredRemarks

    // QC upload
    const [qcFile, setQcFile] = useState(null);

    // Budget checked gate
    const [budgetPassed, setBudgetPassed] = useState(false);

    // ── Init ──────────────────────────────────────────────────────────────────

    useEffect(() => {
        if (roleId && userId) {
            dispatch(fetchSupplierIndents({ roleId, userId, ccType }));
            dispatch(fetchSupplierVendors());
        }
        return () => dispatch(resetCreation());
    }, [roleId, userId, ccType, dispatch]);

    // Sync grid rows when API data arrives — Purchase Price defaults to the
    // item's Standard Price (basicprice), matching the legacy SupplierPOGrid
    // partial (value="@item.basicprice"), not NewBasicprice.
    useEffect(() => {
        if (gridData.length > 0) {
            const initial = {};
            gridData.forEach((item) => {
                initial[item.IndentListId] = {
                    quotedPrice:   '',
                    poQty:         '',
                    purchasePrice: item.basicprice || 0,
                    remarks:       '',
                };
            });
            setRowEdits(initial);
            setSelectedRows({});
            setBudgetPassed(false);
        }
    }, [gridData]);

    // React to part-indent check result
    useEffect(() => {
        if (partIndentCheck !== null) {
            if (partIndentCheck.POExist) {
                setShowPartWarning(true);
            } else {
                doLoadGrid(pendingIndentNo);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [partIndentCheck]);

    // React to save result
    useEffect(() => {
        if (!saveResult) return;
        const parts = String(saveResult).split('$');
        const [status, poNo, filePath] = parts;

        if (status === 'Submited') {
            const finishUp = () => {
                toast.success(`Supplier PO ${poNo} created successfully.`);
                handleReset();
            };
            if (qcFile && filePath) {
                uploadFileToS3(qcFile, S3_FOLDERS.SUPPLIER_PO, filePath)
                    .then(finishUp)
                    .catch(() => { toast.error('PO saved, but the document upload failed.'); finishUp(); });
            } else {
                finishUp();
            }
        } else {
            toast.error(saveResult || 'Failed to save Supplier PO');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveResult]);

    // ── Computed values ───────────────────────────────────────────────────────

    // Only rows the user has checked participate in this PO
    const selectedItems = useMemo(() =>
        gridData.filter((item) => selectedRows[item.IndentListId]),
    [gridData, selectedRows]);

    // NewpricePOTotal — sum of PO Qty * Purchase Price over selected rows
    const newPriceTotal = useMemo(() => selectedItems.reduce((sum, item) => {
        const e = rowEdits[item.IndentListId];
        if (!e) return sum;
        return sum + (parseFloat(e.purchasePrice) || 0) * (parseFloat(e.poQty) || 0);
    }, 0), [selectedItems, rowEdits]);

    // StandardpricePOtotal — sum of PO Qty * Standard (basic) Price over selected rows
    const standardPriceTotal = useMemo(() => selectedItems.reduce((sum, item) => {
        const e = rowEdits[item.IndentListId];
        if (!e) return sum;
        return sum + (parseFloat(item.basicprice) || 0) * (parseFloat(e.poQty) || 0);
    }, 0), [selectedItems, rowEdits]);

    // Mandatory PDF upload once the PO value exceeds the CC's configured limit
    const qcRequired = newPriceTotal > (parseFloat(uploadDocValue) || 0);

    const indentListIds = useMemo(() =>
        selectedItems.map((r) => r.IndentListId).join(',') + (selectedItems.length > 0 ? ',' : ''),
    [selectedItems]);

    // Part indents pending for the same cost center as the currently selected new indent
    const relatedPartIndents = useMemo(() => {
        if (!selectedIndentNo) return [];
        const cc = parseCCPrefix(selectedIndentNo);
        return partIndentList.filter((p) => parseCCPrefix(p.IndentNo) === cc);
    }, [partIndentList, selectedIndentNo]);

    // ── Handlers ──────────────────────────────────────────────────────────────

    const handleIndentSelect = useCallback((indentNo, type) => {
        setSelectedIndentNo(indentNo);
        dispatch(clearPartIndentCheck());
        dispatch(clearGridData());
        setBudgetPassed(false);
        setLoadedIndentNo('');

        if (type === 'new' && indentNo) {
            setPendingIndentNo(indentNo);
            dispatch(checkPartIndentExist(indentNo));
        }
    }, [dispatch]);

    const doLoadGrid = useCallback((indentNo) => {
        if (!indentNo) return;
        setLoadedIndentNo(indentNo);
        dispatch(fetchPOGridData(indentNo));
        dispatch(fetchUploadDocValue(indentNo));
        setPendingIndentNo('');
        setShowPartWarning(false);
    }, [dispatch]);

    const handleLoadAnyway = () => {
        setShowPartWarning(false);
        doLoadGrid(pendingIndentNo);
    };

    const handlePartIndentSelect = useCallback((indentNo) => {
        setSelectedIndentNo(indentNo);
        dispatch(clearPartIndentCheck());
        dispatch(clearGridData());
        setBudgetPassed(false);
        setLoadedIndentNo('');
        if (indentNo) doLoadGrid(indentNo);
    }, [dispatch, doLoadGrid]);

    const handleRowEdit = (id, field, value) => {
        setBudgetPassed(false);

        if (field === 'poQty') {
            const item = gridData.find((g) => g.IndentListId === id);
            const indentQty = parseFloat(item?.Requestedqty ?? item?.quantity) || 0;
            const numVal = parseFloat(value);
            if (indentQty > 0 && !isNaN(numVal) && numVal > indentQty) {
                toast.error(`PO Qty cannot exceed Indent Qty (${indentQty})`);
                value = String(indentQty);
            }
        }

        setRowEdits((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
    };

    const toggleRowSelect = (id) => {
        setBudgetPassed(false);
        setSelectedRows((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const handleHeaderChange = (field, value) => {
        setBudgetPassed(false);
        setHeader((prev) => ({ ...prev, [field]: value }));
        if (field === 'vendorCode') {
            const v = vendors.find((x) => String(x.VendorCode || x.vendorCode) === String(value));
            setHeader((prev) => ({ ...prev, vendorCode: value, vendorName: v?.VendorName || v?.vendorName || '' }));
            // Legacy VendorChange(): GST selection starts over for the new vendor.
            resetGst();
        }
        if (field === 'paymentType') {
            // ThirdParty POs are always Single-MRR and the toggle is locked (legacy PaymenttypeChange()).
            if (value === 'ThirdParty') setHeader((prev) => ({ ...prev, paymentType: value, mrrType: 'Single' }));
        }
    };

    const resetGst = () => {
        setGstApplicable('');
        setCompanyGstList([]);
        setVendorGstList([]);
        setCompanyGst('');
        setVendorGst('');
    };

    // Legacy GSTExistChange() → vendorgst() → Companygst().
    const handleGstApplicableChange = async (value) => {
        setGstApplicable(value);
        setCompanyGst('');
        setVendorGst('');
        if (value !== 'Yes') return;
        if (!header.vendorCode) { toast.error('Select Vendor'); setGstApplicable(''); return; }
        try {
            const vres = await getVendorGSTNos(header.vendorCode);
            const vlist = vres.data?.Data || [];
            if (!vlist.length) {
                toast.error('No Vendor GST Number Found!');
                setGstApplicable('No');
                return;
            }
            setVendorGstList(vlist);
            const cres = await getCompanySupGSTNos();
            const clist = cres.data?.Data || [];
            if (!clist.length) toast.error('No Company GST Number Found!');
            setCompanyGstList(clist);
        } catch (err) {
            toast.error(err?.response?.data?.Message || 'Failed to load GST numbers');
        }
    };

    const handleInvoiceChange = (field, value) => setInvoice((prev) => ({ ...prev, [field]: value }));
    const handleSiteChange = (field, value) => setSite((prev) => ({ ...prev, [field]: value }));

    // T&C line management
    const addTermLine = () =>
        setTermsLines((prev) => [...prev, { id: Date.now(), text: '' }]);

    const updateTermLine = (id, text) =>
        setTermsLines((prev) => prev.map((l) => (l.id === id ? { ...l, text } : l)));

    const removeTermLine = (id) =>
        setTermsLines((prev) => prev.filter((l) => l.id !== id));

    // Predefined T&C flow
    const handlePredefinedExistChange = (value) => {
        setPredefinedTermsExist(value);
        setSelectedHeadId('');
        setSelectedHeadName('');
        setAppliedPreferredLines([]);
        dispatch(clearTermRemarks());
        if (value === 'Yes') dispatch(fetchTermHeads());
    };

    const handleLoadTermHead = () => {
        if (!selectedHeadId || !selectedHeadName) { toast.warning('Please select a T&C head'); return; }
        dispatch(fetchTermRemarks({ headName: selectedHeadName, headId: selectedHeadId }));
    };

    useEffect(() => {
        if (termRemarks.length > 0) {
            setAppliedPreferredLines(termRemarks.map((r) => r.TermRemark || r.Remark || r.Description || String(r)));
        }
    }, [termRemarks]);

    // Budget check
    const runBudgetCheck = async (extraPOAmt) => {
        const result = await dispatch(checkPOBudget({
            IndentListids: indentListIds,
            ExtraPOAmt:    extraPOAmt,
            IndentNo:      loadedIndentNo,
        }));

        if (checkPOBudget.fulfilled.match(result)) {
            const payload = result.payload;
            const message = typeof payload === 'string' ? payload : (payload?.Data || payload?.Message || '');
            // Exact match only — "Insufficient ..." also *contains* "sufficient",
            // so a substring check here would wrongly treat a failed check as passed.
            return message === 'Sufficient';
        }
        return false;
    };

    // Validate and submit
    const handleSubmit = async () => {
        if (!loadedIndentNo)      { toast.error('Please load an indent first'); return; }
        if (!header.vendorCode)   { toast.error('Please select a supplier'); return; }
        if (!header.poDate)       { toast.error('Please enter PO Date'); return; }
        if (!header.poExpireDate) { toast.error('Please enter PO Expiry Date'); return; }
        if (!header.refNo)        { toast.error('Please enter Reference Number'); return; }
        if (!header.refDate)      { toast.error('Please enter Reference Date'); return; }
        if (!header.paymentType)  { toast.error('Please select Payment Type'); return; }
        if (!header.lcApplicable) { toast.error('Please select whether LC is Applicable'); return; }
        if (!gstApplicable) { toast.error('Select Is GST Applicable?'); return; }
        if (gstApplicable === 'Yes') {
            if (!companyGst) { toast.error('Select Company GST No'); return; }
            if (!vendorGst) { toast.error('Select Vendor GST No'); return; }
        }
        if (!selectedItems.length) { toast.error('Please select at least one item to create the PO'); return; }

        const itemsValid = selectedItems.every((item) => {
            const e = rowEdits[item.IndentListId];
            const qty = parseFloat(e?.poQty);
            const price = parseFloat(e?.purchasePrice);
            const quoted = parseFloat(e?.quotedPrice);
            const indentQty = parseFloat(item.Requestedqty ?? item.quantity) || 0;
            return e && qty > 0 && price > 0 && quoted > 0 && qty <= indentQty && (e.remarks || '').trim() !== '';
        });
        if (!itemsValid) {
            toast.error('Every selected item needs PO Qty (≤ Indent Qty), Purchase Price, Quoted Price and Remarks');
            return;
        }

        const termsFilled = termsLines.map((l) => l.text.trim()).filter(Boolean);
        if (!termsFilled.length) { toast.error('Please enter at least one Term & Condition'); return; }

        if (!predefinedTermsExist) { toast.error('Please select whether Predefined Terms apply'); return; }
        if (predefinedTermsExist === 'Yes' && (!selectedHeadId || !appliedPreferredLines.length)) {
            toast.error('Please select a Predefined Terms head and load its terms'); return;
        }

        if (qcRequired && !qcFile) { toast.error(`PO value exceeds the permitted limit (₹${uploadDocValue}) — please upload the PDF document`); return; }
        if (qcFile && getFileExt(qcFile.name) !== 'pdf') { toast.error('Invalid upload file. Only PDF is accepted'); return; }

        let ids = '', poQtys = '', itemCodes = '', purchasePrices = '', standardPrices = '',
            standardAmts = '', purchaseAmts = '', quotedPrices = '', itemRemarks = '';

        selectedItems.forEach((item) => {
            const e = rowEdits[item.IndentListId];
            const qty = parseFloat(e.poQty) || 0;
            const purchasePrice = parseFloat(e.purchasePrice) || 0;
            const standardPrice = parseFloat(item.basicprice) || 0;

            ids             += `${item.IndentListId},`;
            poQtys          += `${qty},`;
            itemCodes       += `${item.itemcode},`;
            purchasePrices  += `${purchasePrice},`;
            standardPrices  += `${standardPrice},`;
            standardAmts    += `${(qty * standardPrice).toFixed(2)},`;
            purchaseAmts    += `${(qty * purchasePrice).toFixed(2)},`;
            quotedPrices    += `${parseFloat(e.quotedPrice) || 0},`;
            itemRemarks     += `${e.remarks.trim() || 'none'},`;
        });

        const payload = {
            PODate:          header.poDate,
            RefNo:           header.refNo,
            RefDate:         header.refDate,
            IndentNo:        loadedIndentNo,
            Indent:          parseIndentSegment(loadedIndentNo),
            CCCode:          parseCCPrefix(loadedIndentNo),
            VendorCode:      header.vendorCode,
            // Legacy: only a Direct PO can be multi-MRR; every other payment type is single MRR.
            Issinglemrr:     header.paymentType !== 'Direct' || header.mrrType === 'Single' ? 1 : 0,
            POExpireDate:    header.poExpireDate,
            // Legacy joins each T&C line with a trailing '|'.
            Remarks:         termsFilled.map((t) => `${t}|`).join(''),
            NewpricePOTotal: newPriceTotal,
            StandardpricePOtotal: standardPriceTotal,

            Indentlistids:   ids,
            POQtys:          poQtys,
            Itemcodes:       itemCodes,
            Purchaseprices:  purchasePrices,
            Standardprices:  standardPrices,
            Indentliststandardamount: standardAmts,
            Indentlistidnewamount:    purchaseAmts,
            Quotedprices:    quotedPrices,
            ItemRemarks:     itemRemarks,

            SiteAddress1:    site.address1,
            SiteAddress2:    site.address2,
            Contact:         site.contact,
            SiteMobileNo:    site.mobileNo,
            InvAddress1:     invoice.address1,
            InvAddress2:     invoice.address2,
            // Legacy sends the GST number (option text before ',') and the option value as the id; 0 when GST is not applicable.
            GstNo:           gstApplicable === 'Yes' ? String(selectedCompanyGst?.GSTText || '').split(',')[0].trim() : '',
            CompanyGstId:    gstApplicable === 'Yes' ? String(companyGst).trim() : 0,
            VenGstNo:        gstApplicable === 'Yes' ? String(selectedVendorGst?.VenGSTText || '').split(',')[0].trim() : '',
            VenGstId:        gstApplicable === 'Yes' ? String(vendorGst).trim() : 0,
            MobileNo:        invoice.mobileNo,

            PaymentType:     header.paymentType,
            LCApplicable:    header.lcApplicable,

            PredefinedTermsExist: predefinedTermsExist,
            ItemTermHeadID:  predefinedTermsExist === 'Yes' ? (parseInt(selectedHeadId, 10) || 0) : 0,
            // Legacy strips '|' from each predefined term and appends a trailing '|'.
            PreferredRemarks: predefinedTermsExist === 'Yes' ? appliedPreferredLines.map((t) => `${String(t).trim().replaceAll('|', '')}|`).join('') : '',

            Filechk:         qcFile ? 'Yes' : 'No',
            Extension:       qcFile ? getFileExt(qcFile.name) : '',

            RoleId:          parseInt(roleId, 10) || 0,
            CreatedBy:       userData?.userName || "",
        };

        // Budget is checked on every submit (no separate Check Budget button).
        if (newPriceTotal > standardPriceTotal) {
            const extraPOAmt = newPriceTotal - standardPriceTotal;
            const passed = await runBudgetCheck(extraPOAmt);
            if (!passed) { toast.error('Budget check failed. Please review amounts.'); setBudgetPassed(false); return; }
            setBudgetPassed(true);
        }

        dispatch(submitSupplierPO(payload));
    };

    const handleReset = () => {
        setSelectedIndentNo('');
        setLoadedIndentNo('');
        setRowEdits({});
        setSelectedRows({});
        setHeader({ vendorCode: '', vendorName: '', poDate: '', poExpireDate: '', refNo: '', refDate: '', paymentType: '', mrrType: 'Single', lcApplicable: '' });
        setInvoice({ address1: '', address2: '', gstNo: '', mobileNo: '' });
        setSite({ address1: '', address2: '', contact: '', mobileNo: '' });
        resetGst();
        setTermsLines([{ id: Date.now(), text: '' }]);
        setPredefinedTermsExist('');
        setSelectedHeadId('');
        setSelectedHeadName('');
        setAppliedPreferredLines([]);
        setQcFile(null);
        setBudgetPassed(false);
        dispatch(clearGridData());
        dispatch(clearPartIndentCheck());
        dispatch(clearBudgetResult());
        dispatch(clearSaveResult());
        dispatch(clearTermRemarks());
        if (roleId && userId) dispatch(fetchSupplierIndents({ roleId, userId, ccType }));
    };

    // ── Render ────────────────────────────────────────────────────────────────

    const gridHasData = gridData.length > 0;

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 space-y-5">

            {/* Page Title */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow">
                        <ShoppingCart className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h1 className="text-lg font-bold text-gray-900 dark:text-white">
                            Supplier PO Creation
                        </h1>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            CC Type: <span className="font-semibold text-indigo-600 dark:text-indigo-400">{ccType}</span>
                        </p>
                    </div>
                </div>
                <button onClick={handleReset}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 transition-colors">
                    <RefreshCw className="w-3.5 h-3.5" /> Reset
                </button>
            </div>

            {/* ── 1. Indent Selection ──────────────────────────────────────── */}
            <SectionCard icon={Search} title="Indent Selection" accent="indigo">
                {/* Tabs */}
                <div className="flex gap-1 p-1 bg-white dark:bg-gray-800 rounded-lg w-fit border border-gray-200 dark:border-gray-700">
                    {['new', 'part'].map((tab) => (
                        <button key={tab}
                            onClick={() => { setActiveTab(tab); setSelectedIndentNo(''); dispatch(clearPartIndentCheck()); dispatch(clearGridData()); setBudgetPassed(false); setLoadedIndentNo(''); }}
                            className={`px-4 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                                activeTab === tab
                                    ? 'bg-indigo-600 text-white shadow'
                                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                            }`}>
                            {tab === 'new' ? 'New Indents' : 'Part Indents'}
                        </button>
                    ))}
                </div>

                {loading.indents ? (
                    <div className="flex items-center gap-2 text-indigo-600 text-sm"><SpinnerIcon /> Loading indents…</div>
                ) : (
                    <div className="flex items-end gap-3 flex-wrap">
                        <div className="flex-1 min-w-[260px]">
                            <Label required>
                                {activeTab === 'new' ? 'Select New Indent' : 'Select Part Indent'}
                            </Label>
                            <div className="relative">
                                <select
                                    className={inputCls}
                                    value={selectedIndentNo}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setSelectedIndentNo(val);
                                        if (activeTab === 'new') handleIndentSelect(val, 'new');
                                        else handlePartIndentSelect(val);
                                    }}>
                                    <option value="">-- Select Indent --</option>
                                    {(activeTab === 'new' ? indentList : partIndentList).map((i) => (
                                        <option key={i.IndentId} value={i.IndentNo}>{i.IndentNo}</option>
                                    ))}
                                </select>
                                <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                                    <ChevronDown className="w-4 h-4 text-gray-400" />
                                </div>
                            </div>
                        </div>

                        {/* Load Items button for new indents (part indents auto-load) */}
                        {activeTab === 'new' && selectedIndentNo && !showPartWarning && (
                            <button
                                onClick={() => {
                                    dispatch(clearPartIndentCheck());
                                    setPendingIndentNo(selectedIndentNo);
                                    dispatch(checkPartIndentExist(selectedIndentNo));
                                }}
                                disabled={loading.partIndentCheck || loading.gridData}
                                className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium flex items-center gap-2 disabled:opacity-50">
                                {(loading.partIndentCheck || loading.gridData) ? <SpinnerIcon /> : <Package className="w-4 h-4" />}
                                Load Items
                            </button>
                        )}

                        {loading.gridData && (
                            <div className="flex items-center gap-2 text-sm text-indigo-600"><SpinnerIcon /> Loading items…</div>
                        )}

                        {loadedIndentNo && (
                            <div className="flex items-center gap-1.5 text-xs text-green-700 dark:text-green-400 font-medium">
                                <CheckCircle className="w-3.5 h-3.5" /> Loaded: {loadedIndentNo}
                            </div>
                        )}
                    </div>
                )}

                {/* Part indent warning */}
                {showPartWarning && (
                    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                            <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                            <div className="flex-1">
                                <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">Part Indent Exists</p>
                                <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                                    A part indent against this cost center already exists — there are pending requirements from a previous PO
                                    for this cost center. Do you still want to create a new PO from this new request?
                                </p>
                                <div className="flex gap-2 mt-3">
                                    <button onClick={handleLoadAnyway}
                                        className="px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold">
                                        Yes, Proceed
                                    </button>
                                    <button onClick={() => { setShowPartWarning(false); setSelectedIndentNo(''); dispatch(clearPartIndentCheck()); }}
                                        className="px-3 py-1.5 rounded-lg bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-xs font-semibold">
                                        No, Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Part indent list reference — only part indents matching the selected indent's CC */}
                {activeTab === 'new' && selectedIndentNo && relatedPartIndents.length > 0 && (
                    <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-700 rounded-lg p-3">
                        <p className="text-xs font-semibold text-indigo-700 dark:text-indigo-300 mb-1">
                            Part Indents pending for this CC ({parseCCPrefix(selectedIndentNo)}):
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {relatedPartIndents.map((p) => (
                                <span key={p.IndentId}
                                    className="px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 rounded text-xs font-mono">
                                    {p.IndentNo}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </SectionCard>

            {/* ── 2. PO Header ─────────────────────────────────────────────── */}
            {gridHasData && (
                <SectionCard icon={Building2} title="PO Header" accent="green">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {/* Supplier */}
                        <div>
                            <Label required>Supplier / Vendor</Label>
                            <div className="relative">
                                <select className={inputCls} value={header.vendorCode}
                                    onChange={(e) => handleHeaderChange('vendorCode', e.target.value)}>
                                    <option value="">-- Select Supplier --</option>
                                    {vendors.map((v) => {
                                        const code = v.VendorCode || v.vendorCode;
                                        const name = v.VendorName || v.vendorName || v.Name;
                                        return <option key={code} value={code}>{name}</option>;
                                    })}
                                </select>
                                <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                                    {loading.vendors ? <SpinnerIcon /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                                </div>
                            </div>
                        </div>

                        {/* PO Date */}
                        <div>
                            <Label required>PO Date</Label>
                            <CustomDatePicker
                                value={header.poDate}
                                onChange={(d) => handleHeaderChange('poDate', d)}
                                format="DD-MMM-YYYY"
                                placeholder="Select PO Date"
                                maxDate={todayFormatted()}
                            />
                        </div>

                        {/* PO Expiry Date */}
                        <div>
                            <Label required>PO Expiry Date</Label>
                            <CustomDatePicker
                                value={header.poExpireDate}
                                onChange={(d) => handleHeaderChange('poExpireDate', d)}
                                format="DD-MMM-YYYY"
                                placeholder="Select Expiry Date"
                                minDate={header.poDate || undefined}
                            />
                        </div>

                        {/* Reference Number */}
                        <div>
                            <Label required>Reference / Quotation No.</Label>
                            <input className={inputCls} placeholder="Enter reference…"
                                value={header.refNo} onChange={(e) => handleHeaderChange('refNo', e.target.value)} />
                        </div>

                        {/* Reference Date */}
                        <div>
                            <Label required>Reference Date</Label>
                            <CustomDatePicker
                                value={header.refDate}
                                onChange={(d) => handleHeaderChange('refDate', d)}
                                format="DD-MMM-YYYY"
                                placeholder="Select Reference Date"
                                maxDate={todayFormatted()}
                            />
                        </div>

                        {/* Payment Type */}
                        <div>
                            <Label required>Payment Type</Label>
                            <div className="relative">
                                <select className={inputCls} value={header.paymentType}
                                    onChange={(e) => handleHeaderChange('paymentType', e.target.value)}>
                                    <option value="">-- Select --</option>
                                    <option value="Direct">Direct</option>
                                    <option value="ThirdParty">Third Party</option>
                                </select>
                                <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                                    <ChevronDown className="w-4 h-4 text-gray-400" />
                                </div>
                            </div>
                        </div>

                        {/* MRR Type — only relevant once a Payment Type is chosen */}
                        {header.paymentType && (
                            <div>
                                <Label required>MRR Type</Label>
                                <div className="flex gap-3 mt-2">
                                    {['Single', 'Multiple'].map((opt) => (
                                        <label key={opt} className={`flex items-center gap-1.5 ${header.paymentType === 'ThirdParty' && opt === 'Multiple' ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                                            <input type="radio" name="mrrType" value={opt}
                                                checked={header.mrrType === opt}
                                                disabled={header.paymentType === 'ThirdParty'}
                                                onChange={() => handleHeaderChange('mrrType', opt)}
                                                className="text-indigo-600" />
                                            <span className="text-sm text-gray-700 dark:text-gray-300">{opt}</span>
                                        </label>
                                    ))}
                                </div>
                                {header.paymentType === 'ThirdParty' && (
                                    <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">Third Party POs are always Single MRR.</p>
                                )}
                            </div>
                        )}

                        {/* LC Applicable */}
                        <div>
                            <Label required>LC Applicable</Label>
                            <div className="flex gap-3 mt-2">
                                {['Yes', 'No'].map((opt) => (
                                    <label key={opt} className="flex items-center gap-1.5 cursor-pointer">
                                        <input type="radio" name="lcApplicable" value={opt}
                                            checked={header.lcApplicable === opt}
                                            onChange={() => handleHeaderChange('lcApplicable', opt)}
                                            className="text-indigo-600" />
                                        <span className="text-sm text-gray-700 dark:text-gray-300">{opt}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>
                </SectionCard>
            )}

            {/* ── 3. Items Grid ─────────────────────────────────────────────── */}
            {gridHasData && (
                <SectionCard icon={Package}
                    title="Item Details"
                    subtitle={`${selectedItems.length} of ${gridData.length} item${gridData.length !== 1 ? 's' : ''} selected`}
                    accent="purple">

                    <div className="overflow-x-auto rounded-lg border border-purple-200 dark:border-purple-700">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
                                    {['Select', '#', 'Item Code', 'Item Name', 'Specification', 'DCA', 'Unit',
                                      'Indent Qty', 'Quoted Price*', 'Standard Price', 'PO Qty*', 'Purchase Price*', 'Amount', 'Remarks*'].map((h) => (
                                        <th key={h} className="px-3 py-2.5 text-left font-semibold whitespace-nowrap">
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {gridData.map((item, idx) => {
                                    const e   = rowEdits[item.IndentListId] || {};
                                    const amt = (parseFloat(e.purchasePrice) || 0) * (parseFloat(e.poQty) || 0);
                                    const isSelected = !!selectedRows[item.IndentListId];
                                    const row = idx % 2 === 0
                                        ? 'bg-white dark:bg-gray-800'
                                        : 'bg-purple-50/40 dark:bg-purple-900/10';
                                    return (
                                        <tr key={item.IndentListId} className={`${row} border-b border-purple-100 dark:border-purple-800 last:border-0`}>
                                            <td className="px-3 py-2">
                                                <input type="checkbox" className="w-4 h-4 accent-indigo-600 cursor-pointer"
                                                    checked={isSelected}
                                                    onChange={() => toggleRowSelect(item.IndentListId)} />
                                            </td>
                                            <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{idx + 1}</td>
                                            <td className="px-3 py-2 font-mono text-gray-800 dark:text-gray-200 whitespace-nowrap">{item.itemcode?.trim()}</td>
                                            <td className="px-3 py-2 text-gray-800 dark:text-gray-200 max-w-[180px]">
                                                <p className="font-medium">{item.itemname}</p>
                                                <p className="text-gray-500 dark:text-gray-400 text-[10px]">{item.subdcacode}</p>
                                            </td>
                                            <td className="px-3 py-2 text-gray-600 dark:text-gray-400 max-w-[160px] text-[10px]">{item.specification}</td>
                                            <td className="px-3 py-2 text-gray-700 dark:text-gray-300 whitespace-nowrap">{item.dcacode}</td>
                                            <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{item.units}</td>
                                            <td className="px-3 py-2 text-right font-semibold text-gray-800 dark:text-gray-200">{item.Requestedqty ?? item.quantity}</td>

                                            {/* Quoted Price */}
                                            <td className="px-2 py-1.5">
                                                <input type="number" min="0" step="0.01"
                                                    className={`${inputCls} w-24 text-right`}
                                                    value={e.quotedPrice ?? ''}
                                                    onChange={(ev) => handleRowEdit(item.IndentListId, 'quotedPrice', ev.target.value)} />
                                            </td>

                                            {/* Standard Price (read-only reference) */}
                                            <td className="px-3 py-2 text-right font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
                                                ₹{(parseFloat(item.basicprice) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                            </td>

                                            {/* PO Qty */}
                                            <td className="px-2 py-1.5">
                                                <input type="number" min="0" step="0.01"
                                                    className={`${inputCls} w-20 text-right`}
                                                    value={e.poQty ?? ''}
                                                    onChange={(ev) => handleRowEdit(item.IndentListId, 'poQty', ev.target.value)} />
                                            </td>

                                            {/* Purchase Price */}
                                            <td className="px-2 py-1.5">
                                                <input type="number" min="0" step="0.01"
                                                    className={`${inputCls} w-24 text-right`}
                                                    value={e.purchasePrice ?? ''}
                                                    onChange={(ev) => handleRowEdit(item.IndentListId, 'purchasePrice', ev.target.value)} />
                                            </td>

                                            {/* Amount (computed) */}
                                            <td className="px-3 py-2 text-right font-semibold text-indigo-700 dark:text-indigo-400 whitespace-nowrap">
                                                ₹{amt.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                            </td>

                                            {/* Remarks (mandatory once the row is selected) */}
                                            <td className="px-2 py-1.5">
                                                <input type="text" placeholder="Notes…"
                                                    className={`${inputCls} w-36`}
                                                    value={e.remarks ?? ''}
                                                    onChange={(ev) => handleRowEdit(item.IndentListId, 'remarks', ev.target.value)} />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                            <tfoot>
                                <tr className="bg-gradient-to-r from-purple-100 to-indigo-100 dark:from-purple-900/30 dark:to-indigo-900/30">
                                    <td colSpan={12} className="px-3 py-2.5 text-right font-bold text-gray-700 dark:text-gray-300 text-xs">
                                        PO Value — selected items only:
                                    </td>
                                    <td className="px-3 py-2.5 text-right font-bold text-lg text-indigo-700 dark:text-indigo-400">
                                        ₹{newPriceTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                    </td>
                                    <td />
                                </tr>
                            </tfoot>
                        </table>
                    </div>

                    {qcRequired && (
                        <div className="flex items-center gap-2 text-xs font-semibold text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-lg px-3 py-2">
                            <AlertTriangle className="w-4 h-4 shrink-0" />
                            PO value exceeds ₹{(parseFloat(uploadDocValue) || 0).toLocaleString('en-IN')} — PDF document upload is mandatory (see below).
                        </div>
                    )}
                </SectionCard>
            )}

            {/* ── 4. Address & GST ─────────────────────────────────────────── */}
            {gridHasData && (
                <SectionCard icon={MapPin} title="Invoice & Delivery Address" accent="teal">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-3">
                            <p className="text-xs font-bold uppercase tracking-wide text-teal-700 dark:text-teal-400">Invoice Address</p>
                            <div>
                                <Label>Address Line 1</Label>
                                <textarea rows={2} className={inputCls}
                                    value={invoice.address1} onChange={(e) => handleInvoiceChange('address1', e.target.value)} />
                            </div>
                            <div>
                                <Label>Address Line 2</Label>
                                <textarea rows={2} className={inputCls}
                                    value={invoice.address2} onChange={(e) => handleInvoiceChange('address2', e.target.value)} />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>Is GST Applicable?</Label>
                                    <select className={inputCls} value={gstApplicable}
                                        onChange={(e) => handleGstApplicableChange(e.target.value)}>
                                        <option value="">-Please Select-</option>
                                        <option value="Yes">Yes</option>
                                        <option value="No">No</option>
                                    </select>
                                </div>
                                <div>
                                    <Label>Mobile No.</Label>
                                    <input className={`${inputCls} font-mono`} maxLength={10} value={invoice.mobileNo}
                                        onChange={(e) => handleInvoiceChange('mobileNo', e.target.value.replace(/\D/g, ''))} />
                                </div>
                            </div>
                            {gstApplicable === 'Yes' && (
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label>Company GST No.</Label>
                                        <select className={inputCls} value={companyGst} onChange={(e) => setCompanyGst(e.target.value)}>
                                            <option value="">-Please Select-</option>
                                            {companyGstList.map((g) => <option key={g.GSTVal} value={g.GSTVal}>{g.GSTText}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <Label>Vendor GST No.</Label>
                                        <select className={inputCls} value={vendorGst} onChange={(e) => setVendorGst(e.target.value)}>
                                            <option value="">-Please Select-</option>
                                            {vendorGstList.map((g) => <option key={g.VenGSTVal} value={g.VenGSTVal}>{g.VenGSTText}</option>)}
                                        </select>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="space-y-3">
                            <p className="text-xs font-bold uppercase tracking-wide text-teal-700 dark:text-teal-400 flex items-center gap-1.5">
                                <Truck className="w-3.5 h-3.5" /> Delivery Site Address
                            </p>
                            <div>
                                <Label>Address Line 1</Label>
                                <textarea rows={2} className={inputCls}
                                    value={site.address1} onChange={(e) => handleSiteChange('address1', e.target.value)} />
                            </div>
                            <div>
                                <Label>Address Line 2</Label>
                                <textarea rows={2} className={inputCls}
                                    value={site.address2} onChange={(e) => handleSiteChange('address2', e.target.value)} />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>Contact Person</Label>
                                    <input className={inputCls} value={site.contact}
                                        onChange={(e) => handleSiteChange('contact', e.target.value)} />
                                </div>
                                <div>
                                    <Label>Mobile No.</Label>
                                    <input className={`${inputCls} font-mono`} maxLength={10} value={site.mobileNo}
                                        onChange={(e) => handleSiteChange('mobileNo', e.target.value.replace(/\D/g, ''))} />
                                </div>
                            </div>
                        </div>
                    </div>
                </SectionCard>
            )}

            {/* ── 5. Terms & Conditions ────────────────────────────────────── */}
            {gridHasData && (
                <SectionCard icon={BookOpen} title="Terms & Conditions" accent="amber">
                    <div className="flex gap-2 flex-wrap">
                        <button onClick={addTermLine}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold">
                            <Plus className="w-3.5 h-3.5" /> Add Line
                        </button>
                    </div>

                    <div className="space-y-2">
                        {termsLines.map((line, idx) => (
                            <div key={line.id} className="flex items-center gap-2">
                                <span className="text-xs text-gray-400 dark:text-gray-500 w-5 text-right shrink-0">{idx + 1}.</span>
                                <input type="text" placeholder={`Term / Condition ${idx + 1}…`}
                                    className={`${inputCls} flex-1`}
                                    value={line.text}
                                    onChange={(e) => updateTermLine(line.id, e.target.value)} />
                                {termsLines.length > 1 && (
                                    <button onClick={() => removeTermLine(line.id)}
                                        className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500 transition-colors">
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>

                    <div className="pt-3 border-t border-amber-200 dark:border-amber-800 space-y-3">
                        <div>
                            <Label required>Predefined Terms Exist?</Label>
                            <div className="flex gap-3 mt-1">
                                {['Yes', 'No'].map((opt) => (
                                    <label key={opt} className="flex items-center gap-1.5 cursor-pointer">
                                        <input type="radio" name="preTermExist" value={opt}
                                            checked={predefinedTermsExist === opt}
                                            onChange={() => handlePredefinedExistChange(opt)}
                                            className="text-amber-600" />
                                        <span className="text-sm text-gray-700 dark:text-gray-300">{opt}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {predefinedTermsExist === 'Yes' && (
                            <div className="flex items-end gap-3 flex-wrap">
                                <div className="flex-1 min-w-[220px]">
                                    <Label required>T&amp;C Head Name</Label>
                                    <div className="relative">
                                        <select className={inputCls}
                                            value={selectedHeadId}
                                            onChange={(e) => {
                                                const opt = e.target.options[e.target.selectedIndex];
                                                setSelectedHeadId(e.target.value);
                                                setSelectedHeadName(opt.dataset.name || '');
                                                setAppliedPreferredLines([]);
                                                dispatch(clearTermRemarks());
                                            }}>
                                            <option value="">-- Select Head --</option>
                                            {termHeads.map((h) => {
                                                const id   = h.HeadId   || h.headId   || h.ID   || h.ItemId;
                                                const name = h.HeadName || h.headName || h.Name || h.ItemHeadName;
                                                return (
                                                    <option key={id} value={id} data-name={name}>{name}</option>
                                                );
                                            })}
                                        </select>
                                        <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                                            {loading.termHeads ? <SpinnerIcon /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                                        </div>
                                    </div>
                                </div>
                                <button onClick={handleLoadTermHead}
                                    disabled={loading.termRemarks || !selectedHeadId}
                                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold disabled:opacity-50">
                                    {loading.termRemarks ? <SpinnerIcon /> : <Search className="w-4 h-4" />}
                                    Load Terms
                                </button>
                            </div>
                        )}

                        {predefinedTermsExist === 'Yes' && appliedPreferredLines.length > 0 && (
                            <div className="space-y-1.5 border border-amber-200 dark:border-amber-800 rounded-lg p-3 bg-white dark:bg-gray-800">
                                <p className="text-xs font-semibold text-gray-600 dark:text-gray-400">
                                    {appliedPreferredLines.length} predefined term(s) will be attached:
                                </p>
                                {appliedPreferredLines.map((t, i) => (
                                    <p key={i} className="text-xs text-gray-700 dark:text-gray-300">{i + 1}. {t}</p>
                                ))}
                            </div>
                        )}
                    </div>
                </SectionCard>
            )}

            {/* ── 6. QC Upload (conditional) ────────────────────────────────── */}
            {gridHasData && qcRequired && (
                <SectionCard icon={Upload} title="Document Upload" subtitle={`Required — PO value > ₹${(parseFloat(uploadDocValue) || 0).toLocaleString('en-IN')}`} accent="rose">
                    <div className="flex items-center gap-4 flex-wrap">
                        <label className="flex items-center gap-2 px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-medium cursor-pointer">
                            <Upload className="w-4 h-4" />
                            {qcFile ? 'Change File' : 'Choose File'}
                            <input type="file" accept=".pdf" className="hidden"
                                onChange={(e) => setQcFile(e.target.files[0] || null)} />
                        </label>
                        {qcFile ? (
                            <div className="flex items-center gap-2 text-sm">
                                <CheckCircle className="w-4 h-4 text-green-600" />
                                <span className="text-gray-700 dark:text-gray-300">{qcFile.name}</span>
                                <span className="text-xs text-gray-400">({(qcFile.size / 1024).toFixed(1)} KB)</span>
                                <button onClick={() => setQcFile(null)}
                                    className="p-0.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500">
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        ) : (
                            <span className="text-xs text-rose-600 dark:text-rose-400">No file chosen — PDF only</span>
                        )}
                    </div>
                </SectionCard>
            )}

            {/* ── 7. Budget Check & Submit ──────────────────────────────────── */}
            {gridHasData && (
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="flex items-center gap-4 flex-wrap">
                            {/* Budget status badge */}
                            {newPriceTotal > standardPriceTotal && budgetPassed && (
                                <span className="flex items-center gap-1.5 text-xs font-semibold text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20 border border-green-300 dark:border-green-700 px-3 py-1.5 rounded-full">
                                    <CheckCircle className="w-3.5 h-3.5" /> Budget OK
                                </span>
                            )}
                            {errors.budget && (
                                <span className="flex items-center gap-1.5 text-xs font-semibold text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 px-3 py-1.5 rounded-full">
                                    <AlertTriangle className="w-3.5 h-3.5" /> Budget Failed
                                </span>
                            )}
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                                PO Value: <span className="font-bold text-indigo-700 dark:text-indigo-400">
                                    ₹{newPriceTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                </span>
                            </div>
                        </div>

                        <div className="flex items-center gap-3 flex-wrap">
                            <button onClick={handleSubmit}
                                disabled={loading.save || loading.budget}
                                className="flex items-center gap-2 px-5 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold disabled:opacity-50 transition-colors">
                                {(loading.save || loading.budget) ? <SpinnerIcon /> : <Send className="w-4 h-4" />}
                                {loading.budget ? 'Checking Budget…' : loading.save ? 'Saving…' : 'Save Supplier PO'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SupplierPOCreation;
